import React, { useEffect, useState } from "react";
import { useT } from "../helpers/useT";
import {
  constructAthenaUrl,
  HttpResponseError,
  ListResponseWithIncluded,
  Resource,
  sendRequest,
  SingleResponse,
  useFetch,
} from "../api/useFetch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../components/List/Table";
import { Headline } from "../components/Headline";
import { EmptyListPlaceholder } from "../components/List/EmptyList";
import { ListFilters, Pagination } from "../components/List/List";
import DetailsLink from "../components/DetailsLink";
import { Location, Offer } from "./offer";
import { DistanceOrDuration } from "./components/DistanceOrDuration";
import CustomDate from "../components/Date";
import { ApiResponseAware } from "../api/ApiResponseAware";
import { tabs } from "./ride";
import Tabs from "../components/Tabs";
import { useMe } from "../me";
import Price from "../components/Price";
import { ServiceClassWithTitle } from "../components/ServiceClass";
import RefreshList from "../list/Refresh";
import PropertyWithTitle from "../components/PropertyWithTitle";
import { trackError } from "../tracking/error";

export type OffersResponse = ListResponseWithIncluded<Offer, Location>;
export type OfferResponse = SingleResponse<Offer>;

export function OfferList() {
  const me = useMe();
  const t = useT();
  const [currentPage, setCurrentPage] = useState(1);

  const apiCall = useFetch(
    constructAthenaUrl("/hades/offers", {
      "page[number]": `${currentPage}`,
      "page[size]": "30",
      include: "pickup_location,dropoff_location",
    }),
  );

  return (
    <>
      <Headline title={t("rides.title")} flex={true}>
        <RefreshList action={() => apiCall.reload()} />
      </Headline>
      <Tabs tabs={tabs(me)} />
      <ApiResponseAware
        apiCall={apiCall}
        render={(response: OffersResponse) => (
          <OffersTable
            response={response}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      />
    </>
  );
}

function OffersTable({
  response,
  currentPage,
  setCurrentPage,
}: {
  response: OffersResponse;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}) {
  const t = useT();
  const [goneOfferIds, setGoneOfferIds] = useState<string[]>([]);

  const offers = response.data.filter((o) => !goneOfferIds.includes(o.id));
  const offersRemovedCount = response.data.length - offers.length;

  return (
    <>
      <ListFilters
        recordCount={t("offers.record-count", {
          number: response.meta.record_count - offersRemovedCount,
        })}
      />
      <EmptyListPlaceholder data={offers} title={t("offers.empty-list")}>
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeaderCell width={150}>{t("shared.date")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.pickup")}</TableHeaderCell>
              <TableHeaderCell>{t("shared.dropoff")}</TableHeaderCell>
              <TableHeaderCell width={90}>
                {t("shared.service-class")}
              </TableHeaderCell>
              <TableHeaderCell width={80}>{t("shared.price")}</TableHeaderCell>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <OfferRow
                key={offer.id}
                originalOffer={offer}
                included={response.included}
                onOfferGone={() =>
                  setGoneOfferIds((prevState) => [...prevState, offer.id])
                }
              />
            ))}
          </TableBody>
        </Table>
        <Pagination
          currentPage={currentPage}
          pageCount={response.meta.page_count}
          onPageClick={setCurrentPage}
        />
      </EmptyListPlaceholder>
    </>
  );
}

function OfferRow({
  originalOffer,
  included,
  onOfferGone,
}: {
  originalOffer: Resource<Offer>;
  included: Array<Resource<Location>> | undefined;
  onOfferGone: () => void;
}) {
  const [offer, setOffer] = useState<Resource<Offer>>(originalOffer);

  useEffect(() => {
    const timer = setTimeout(
      () => {
        refreshOffer(offer.id);
      },
      (offer.attributes.next_price_change_in + 2) * 1000,
    ); // the +2 is due to delays in backend to actually having the next update ready, which currently causes refetching multiple times in a row. not perfect, but better than before

    return () => clearTimeout(timer);
  }, [offer.id, offer.attributes.next_price_change_in]);

  const refreshOffer = async (id: string) => {
    try {
      const result = await sendRequest(
        constructAthenaUrl(`/hades/offers/${id}`),
      );
      const response = (await result.json()) as OfferResponse;
      setOffer(response.data);
    } catch (e) {
      if (e instanceof HttpResponseError && e.status === 404) {
        onOfferGone();
      } else {
        trackError("Error refreshing offer", e);
      }
    }
  };

  return (
    <TableRow key={offer.id}>
      <TableCell>
        <PropertyWithTitle isList title="shared.date">
          <CustomDate value={offer.attributes.starts_at} isHighlighted />
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.pickup">
          {
            included?.find(
              (l) => l.id === offer.relationships?.pickup_location?.data.id,
            )?.attributes.formatted_address_en
          }
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.dropoff">
          {
            included?.find(
              (l) => l.id === offer.relationships?.dropoff_location?.data.id,
            )?.attributes.formatted_address_en
          }
        </PropertyWithTitle>
      </TableCell>
      <TableCell>
        <ServiceClassWithTitle id={offer.attributes.service_class} />
      </TableCell>
      <TableCell>
        <PropertyWithTitle isList title="shared.price">
          <Price
            currency={offer.attributes.currency}
            value={parseFloat(offer.attributes.price)}
          />
          <DistanceOrDuration ride={offer.attributes} />
          <DetailsLink section="offers" qs={{ id: offer.id }} />
        </PropertyWithTitle>
      </TableCell>
    </TableRow>
  );
}
